// [S-061c] Briefs index — lists composed briefs newest-first; each row
// links to the detail page. The "+ Compose new" CTA navigates to
// /briefs/new.
import { Link } from "react-router-dom";
import { Alert, PageHead } from "../components";
import { useBriefs, type Brief } from "../data/briefsQuery";

export function Briefs(): JSX.Element {
  const { data, isLoading, isFetching, isError, error } = useBriefs();
  const briefs = data ?? [];

  return (
    <>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot cert" aria-hidden="true" />
            Pre-negotiation briefs
          </>
        }
        title={
          <>
            Past <em>conversations</em>, surfaced.
          </>
        }
        sub={
          isLoading
            ? "Loading…"
            : `${briefs.length.toLocaleString()} brief${briefs.length === 1 ? "" : "s"} composed.`
        }
        cta={
          <Link to="/briefs/new" className="brf__compose-cta">
            + Compose new brief
          </Link>
        }
      />

      {(isLoading || (isFetching && briefs.length === 0)) && (
        <div className="prec__state" role="status" aria-live="polite">
          Loading briefs…
        </div>
      )}

      {isError && (
        <Alert label="Couldn't load briefs" tone="error">
          {error.message}
        </Alert>
      )}

      {!isLoading && !isFetching && !isError && briefs.length === 0 && (
        <div className="prec__state">
          <div className="prec__state__title">No briefs yet</div>
          <p>
            Compose your first pre-negotiation brief — it'll combine the
            certified templates with your studio's prior deals.
          </p>
          <Link to="/briefs/new" className="prec__state__cta">
            Compose brief →
          </Link>
        </div>
      )}

      {!isLoading && !isError && briefs.length > 0 && (
        <div className="brf__index" role="list">
          {briefs.map((b) => (
            <BriefCard key={b.id} brief={b} />
          ))}
        </div>
      )}
    </>
  );
}

function BriefCard({ brief }: { brief: Brief }): JSX.Element {
  return (
    <Link
      to={`/briefs/${brief.id}`}
      role="listitem"
      className="brf__card"
      data-testid="brief-row"
    >
      <header className="brf__card__head">
        <span className="brf__card__title">
          {prettyGuild(brief.input.guild)} ·{" "}
          {prettyDealType(brief.input.dealType)}
        </span>
        <span className="brf__card__date">{formatDate(brief.composedAt)}</span>
      </header>
      <p className="brf__card__terms">{brief.input.keyTermsInFlight}</p>
      <footer className="brf__card__layers">
        <span className={brief.l1Section !== null ? "src-dot cert on" : "src-dot cert off"} />
        <span>L1</span>
        <span className={brief.l2Section !== null ? "src-dot stud on" : "src-dot stud off"} />
        <span>L2</span>
      </footer>
    </Link>
  );
}

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
